import {
  useCallback
} from "react";
import { ButtonBar } from "@k8slens/lds";
import { Transition } from "@headlessui/react";

import { planMetadata, PlanCode } from "src/services/plans";

import Button from "src/components/Button/TrackedButton";
import FeatureList from "src/components/FeatureList/FeatureList";
import LicenseImage from "src/components/LicenseImage/LicenseImage";

import { TrialState } from "./common";

import commonStyles from "./ChoosePlan.module.css";

const getTrialText = (state: TrialState | null) => {
  switch (state) {
    case "used":
      return "Free 30-day trial used";
    case "error":
      return "Not available";
    case "in-use":
      return "Free 30-day trial active";
    default:
      return "Start 30-day free trial";
  }
};

export interface Props {
  loading?: boolean;
  annual: boolean;
  trialState: TrialState | null;
  onChoosePlan(planCode: PlanCode): Promise<boolean>;
}

const ChoosePro: React.FC<Props> = ({
  loading,
  annual,
  trialState,
  onChoosePlan
}) => {
  const planData = planMetadata.pro;

  const handleChooseProTrial = useCallback(() => onChoosePlan("pro-trial"), [onChoosePlan]);

  const handleChoosePro = useCallback(() => {
    const planCode = annual ? "pro-yearly" : "pro-monthly";

    onChoosePlan(planCode);
  }, [annual, onChoosePlan]);

  return (
    <>
      <section className={commonStyles.planInfo}>
        <LicenseImage type="pro" className={commonStyles.licenseImage} />
        <div className={commonStyles.price} aria-label="Price">
          <Transition
            show={annual}
            className={commonStyles.withFade}
            enter="transition-opacity duration-100 delay-150"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <span>{planMetadata.pro.price.annual.text}</span>
            <small>
              per seat /
              <br aria-hidden />
              year
            </small>
          </Transition>
          <Transition
            show={!annual}
            className={commonStyles.withFade}
            enter="transition-opacity duration-100 delay-150"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <span>{planMetadata.pro.price.monthly.text}</span>
            <small>
              per seat /
              <br aria-hidden />
              month
            </small>
          </Transition>
        </div>
        <ButtonBar type="stacked" className={commonStyles.actions}>
          <Button
            loading={loading}
            label={getTrialText(trialState)}
            id={trialState === "in-use" ? "current-plan" : ""}
            primary={trialState === "available"}
            type={trialState === "error" ? "danger" : "default"}
            discreet
            disabled={trialState !== "available"}
            loadingStateOnPromise
            onClick={handleChooseProTrial}
            allCaps
          />
          <Button
            label="Choose"
            primary
            onClick={handleChoosePro}
            aria-label="Choose Pro Plan"
            allCaps
          />
        </ButtonBar>
      </section>
      <section>
        <FeatureList
          className={commonStyles.featureList}
          title={`Included in Lens ${planData.nameShort}`}
          features={planData.features}
        />
      </section>
    </>
  );
};

export default ChoosePro;
